import { z } from 'zod';

/**
 * One Gmail attachment this turn imported into Aline. `documentId` is always a
 * server-created document — never an id read out of model text or vendor
 * output. `sourceKey` identifies the exact vendor artifact the document came
 * from, so a replayed or retried tool result imports it at most once.
 */
const ImportedExternalFileSchema = z.object({
  documentId: z.uuid(),
  sourceKey: z.string().min(1),
});

/**
 * A Gmail message this turn actually observed, with only the recipient
 * addresses that may become collaborators (To/Cc — never From, Sender, Bcc,
 * or anything quoted in the body).
 */
const ObservedMessageSchema = z.object({
  addresses: z.array(z.string().min(1)),
  attachmentIds: z.array(z.string().min(1)),
  messageId: z.string().min(1),
});

/**
 * What the turn learned about which message the user meant. `selected*` is the
 * narrowed set an action may act on; `hasAmbiguousSelection` records that a
 * response covered several messages, so downstream materialization must not
 * guess one.
 */
const MessageRecipientEvidenceSchema = z.object({
  hasAmbiguousSelection: z.boolean(),
  messages: z.array(ObservedMessageSchema),
  selectedAttachmentIds: z.array(z.string().min(1)),
  selectedMessageIds: z.array(z.string().min(1)),
});

/**
 * The server-verified external-reach facts persisted with the turn. Everything
 * here is produced by Aline (document creation, org-scoped user lookup,
 * canonically attributed vendor results) so a resume rebuilds exactly the same
 * trusted context instead of re-deriving it from replayed model text.
 */
export const ExternalReachStateSchema = z.object({
  importedFiles: z.array(ImportedExternalFileSchema),
  messageEvidence: MessageRecipientEvidenceSchema,
});

export type ExternalReachState = z.infer<typeof ExternalReachStateSchema>;
export type ImportedExternalFile = z.infer<typeof ImportedExternalFileSchema>;
export type MessageRecipientEvidence = z.infer<typeof MessageRecipientEvidenceSchema>;
export type ObservedMessage = z.infer<typeof ObservedMessageSchema>;

export function createEmptyExternalReachState(): ExternalReachState {
  return {
    importedFiles: [],
    messageEvidence: {
      hasAmbiguousSelection: false,
      messages: [],
      selectedAttachmentIds: [],
      selectedMessageIds: [],
    },
  };
}

/**
 * Fold a newly materialized delta into the turn's persisted state. Union
 * semantics throughout: a retried activity or replayed tool result must not
 * duplicate an id, and prior evidence must survive every later delta.
 * Ambiguity is sticky — once a response proved the selection was unclear, a
 * later narrower response does not retroactively make it safe.
 */
export function mergeExternalReachState({
  delta,
  prior,
}: {
  delta: ExternalReachState;
  prior: ExternalReachState;
}): ExternalReachState {
  return {
    importedFiles: mergeImportedFiles(prior.importedFiles, delta.importedFiles),
    messageEvidence: {
      hasAmbiguousSelection:
        prior.messageEvidence.hasAmbiguousSelection || delta.messageEvidence.hasAmbiguousSelection,
      messages: mergeObservedMessages(
        prior.messageEvidence.messages,
        delta.messageEvidence.messages
      ),
      selectedAttachmentIds: mergeStrings(
        prior.messageEvidence.selectedAttachmentIds,
        delta.messageEvidence.selectedAttachmentIds
      ),
      selectedMessageIds: mergeStrings(
        prior.messageEvidence.selectedMessageIds,
        delta.messageEvidence.selectedMessageIds
      ),
    },
  };
}

function mergeImportedFiles(
  prior: ImportedExternalFile[],
  delta: ImportedExternalFile[]
): ImportedExternalFile[] {
  const bySourceKey = new Map(prior.map(file => [file.sourceKey, file]));
  for (const file of delta) {
    if (!bySourceKey.has(file.sourceKey)) bySourceKey.set(file.sourceKey, file);
  }
  return [...bySourceKey.values()];
}

function mergeObservedMessages(
  prior: ObservedMessage[],
  delta: ObservedMessage[]
): ObservedMessage[] {
  const byMessageId = new Map(prior.map(message => [message.messageId, message]));
  for (const message of delta) {
    const existing = byMessageId.get(message.messageId);
    byMessageId.set(
      message.messageId,
      existing
        ? {
            addresses: mergeStrings(existing.addresses, message.addresses),
            attachmentIds: mergeStrings(existing.attachmentIds, message.attachmentIds),
            messageId: message.messageId,
          }
        : message
    );
  }
  return [...byMessageId.values()];
}

function mergeStrings(prior: string[], delta: string[]): string[] {
  return [...new Set([...prior, ...delta])];
}
